<template>
    <NavigationTabItem class="reduced-tab" data-testid="companySelector" :tab="reducedCompanySelectorProp" selector />
</template>

<script setup lang="tsx">
    import {computed, defineComponent, h} from 'vue';
    import {SwapCompanyIcon} from '@navbar-logged/common/icons';
    import {useUserStore} from '@navbar-logged/features/user';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import {type NavigationTab} from '..';
    import FreelancerAvatar from './FreelancerAvatar.vue';
    import NavigationTabItem from './NavigationTabItem.vue';
    import UmbrellaAvatar from './UmbrellaAvatar.vue';

    const {t} = useTranslation();

    const userStore = useUserStore();
    const {selectedIdentity, userIdentities, hasFreelancerIdentity, hasUmbrellaIdentity} = storeToRefs(userStore);

    function logout() {
        userStore.logoutUser();
    }

    // - Reduced mode

    const reducedCompanySelectorProp = computed((): NavigationTab => {
        const logoutLink = {
            id: 'company-selector-logout',
            title: t('navbar.aside.tabs.title.logout'),
            separator: 'top',
            customHoverColor: 'var(--joy-color-primary-10)',
            customStyle: {
                color: 'var(--joy-color-primary-50)',
            },
            sticky: true,
            icons: {
                prefixIcon: defineComponent(() => () => h(<VJoyIcon name="tab-inverted" color="error" />)),
            },
            action: () => logout(),
        } as const;

        if (userStore.selectedIdentity && userIdentities.value) {
            let childrenTabs = userIdentities.value?.map(
                (identity): NavigationTab => ({
                    id: identity.id,
                    title: identity.label,
                    action: () => userStore.setUserIdentity(identity.id),
                    isSelected: () => identity.id === selectedIdentity.value?.id,
                    icons: {
                        prefixIcon:
                            identity?.type === 'FREELANCE'
                                ? defineComponent(() => () => h(<FreelancerAvatar avatarSize="24px" />))
                                : identity?.type === 'UMBRELLA'
                                  ? defineComponent(() => () => h(<UmbrellaAvatar avatarSize="24px" />))
                                  : undefined,
                    },
                }),
            );

            if (!hasFreelancerIdentity.value && !hasUmbrellaIdentity.value) {
                childrenTabs.push({
                    id: 'BecomeFreelancer',
                    title: t('navbar.aside.tabs.title.become.freelancer'),
                    separator: userIdentities.value.length > 0 ? 'top' : undefined,
                    url: {
                        href: '/profile/freelancer-signup',
                        blank: true,
                    },
                    customStyle: {
                        color: 'var(--joy-color-secondary-30)',
                        fontWeight: 'bold',
                    },
                });
            }

            childrenTabs = childrenTabs.concat([logoutLink]);

            return {
                id: 'CompanySelectorSwitch',
                title: userStore.selectedIdentity.label,
                icons: {
                    mainIcon:
                        selectedIdentity.value?.type === 'FREELANCE'
                            ? FreelancerAvatar
                            : selectedIdentity.value?.type === 'UMBRELLA'
                              ? UmbrellaAvatar
                              : SwapCompanyIcon,
                    suffixIcon: defineComponent(() => () => h(<VJoyIcon name="chevron-double" color="neutral" size="xsmall" />)),
                },
                isAlwaysDropdown: true,
                dropdownPlacement: 'right-start',
                triggerMode: 'click',
                children: childrenTabs,
            };
        }
        // Display placeholder until user info is fetched
        return {
            id: 'CompanySelectorSwitch',
            title: 'Select a company',
            icons: {
                mainIcon: SwapCompanyIcon,
                suffixIcon: defineComponent(() => () => h(<VJoyIcon name="chevron-double" color="neutral" size="xsmall" />)),
            },
            children: [logoutLink],
            isAlwaysDropdown: true,
            dropdownPlacement: 'right-start',
            triggerMode: 'click',
        };
    });
</script>
